import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Spinner } from 'react-bootstrap';
import Icon from '../../common/Icons';
import MemberRowActionMenu from '../../members/MemberRowActionMenu';
import {
  formatMemberCompanyLine,
  formatMemberFullName,
  formatMemberMetaLine,
  getMemberTypeClass,
  getMemberTypeLabel,
  isMemberActive
} from '../../members/memberListUtils';
import './MemberHierarchyTable.css';

function isAgentMember(member) {
  return member?.type === 'AGENT';
}

function MemberRow({
  member,
  isChild,
  isSelected = false,
  isMenuOpen = false,
  expandCell,
  getCompanyName,
  formatDate,
  onOpenMenu,
  menuCanToggle,
  uiVariant = 'personnel'
}) {
  const active = isMemberActive(member);
  const fullName = formatMemberFullName(member);
  const metaLine = formatMemberMetaLine(member, { isChild, formatDate });
  const company = formatMemberCompanyLine(member, getCompanyName);
  const isAdminUi = uiVariant === 'admin';

  return (
    <tr
      className={[
        isChild ? 'ap-mem-row--child' : 'ap-mem-row--agent',
        active ? '' : 'ap-mem-row--inactive',
        isSelected ? 'ap-mem-row--selected' : '',
        isMenuOpen ? 'adm-table-row--menu-open' : ''
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <td className={`ap-mem-col-expand ${isAdminUi ? 'ap-mem-mobile-expand' : ''}`}>
        {expandCell}
      </td>
      <td
        className={`ap-mem-cell-identity ${isAdminUi ? 'adm-mobile-card-head' : ''}`}
        data-label={isAdminUi ? 'Adhérent' : undefined}
      >
        <div className={`ap-mem-identity ${isChild ? 'ap-mem-identity--child' : ''}`}>
          <div className="ap-mem-identity__head">
            <span className="ap-mem-identity__name">{fullName}</span>
            <span
              className={`ap-mem-status-dot ${active ? 'is-active' : 'is-inactive'}`}
              title={active ? 'Actif' : 'Inactif'}
              aria-label={active ? 'Actif' : 'Inactif'}
            />
          </div>
          {metaLine ? (
            <span className="ap-mem-identity__meta">{metaLine}</span>
          ) : (
            <span className="ap-mem-identity__meta ap-mem-identity__meta--muted">
              Informations complémentaires non renseignées
            </span>
          )}
        </div>
      </td>
      <td
        className="ap-mem-cell-assignment"
        data-label={isAdminUi ? 'Affectation' : undefined}
      >
        <div className="ap-mem-assignment">
          <span
            className={`ap-mem-company-line ${company.empty ? 'ap-mem-company-line--muted' : ''}`}
            title={company.title}
          >
            {company.text}
          </span>
          <span className={`ap-mem-type ${getMemberTypeClass(member.type)}`}>
            {getMemberTypeLabel(member.type)}
          </span>
        </div>
      </td>
      <td
        className={`ap-mem-table__actions-col ${isAdminUi ? 'adm-mobile-card-actions' : ''}`}
        data-label={isAdminUi ? 'Actions' : undefined}
      >
        <button
          type="button"
          className={`ap-mem-action-btn ${isAdminUi ? 'adm-action-btn adm-mobile-action-btn' : ''} ${
            isMenuOpen ? 'is-open' : ''
          }`}
          onClick={(e) => onOpenMenu(e, member, menuCanToggle)}
          aria-label={`Actions pour ${fullName}`}
          aria-expanded={isMenuOpen}
          aria-haspopup="menu"
          title="Actions"
        >
          <Icon name="ellipsis-vertical" size={16} />
        </button>
      </td>
    </tr>
  );
}

function AgentExpandButton({ agent, isExpanded, isLoading, onToggle }) {
  const count = Number(agent.dependent_count) || 0;
  const label = isExpanded
    ? 'Masquer les ayants-droit'
    : count > 0
      ? `Afficher ${count} ayant${count > 1 ? 's' : ''}-droit`
      : 'Afficher les ayants-droit';

  return (
    <button
      type="button"
      className={`ap-mem-expand-btn ${isExpanded ? 'is-expanded' : ''}`}
      onClick={() => onToggle(agent.id)}
      title={label}
      aria-expanded={isExpanded}
      aria-label={label}
      disabled={isLoading}
    >
      {isLoading ? (
        <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />
      ) : (
        <>
          <Icon
            name={isExpanded ? 'chevronDown' : 'chevronRight'}
            size={14}
            className="ap-mem-expand-btn__chevron"
          />
          {count > 0 && (
            <span className="ap-mem-expand-btn__count" aria-hidden>
              {count}
            </span>
          )}
        </>
      )}
    </button>
  );
}

const MemberHierarchyTable = ({
  members,
  hierarchical = true,
  loading,
  expandedAgents,
  loadingDependents,
  onToggleAgent,
  onView,
  onToggleStatus,
  onArchive,
  onRestore,
  onDelete,
  getCompanyName,
  formatDate,
  isArchiveView = false,
  selectable = false,
  selectedIds = null,
  onToggleSelect,
  onToggleSelectAll,
  allSelected = false,
  uiVariant = 'personnel'
}) => {
  const [actionMember, setActionMember] = useState(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [menuCanToggle, setMenuCanToggle] = useState(true);
  const selectAllRef = useRef(null);

  const closeActionMenu = useCallback(() => {
    setActionMember(null);
    setActionMenuAnchor(null);
  }, []);

  const openMenu = (event, member, canToggle = true) => {
    if (actionMember?.id === member.id && actionMenuAnchor) {
      closeActionMenu();
      return;
    }
    setActionMenuAnchor(event.currentTarget);
    setActionMember(member);
    setMenuCanToggle(canToggle);
  };

  const rootMembers = hierarchical ? members.filter(isAgentMember) : members;
  const isAdminUi = uiVariant === 'admin';

  const pageIds = rootMembers.map((m) => m.id);
  const pageAllSel = pageIds.length > 0 && pageIds.every((id) => selectedIds?.has(id));
  const pageSomeSel = !pageAllSel && pageIds.some((id) => selectedIds?.has(id));

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = pageSomeSel;
    }
  }, [pageSomeSel, pageAllSel]);

  if (loading) {
    return (
      <div className="ap-mem-state">
        <Spinner animation="border" variant="primary" size="sm" />
        <p className="mt-3 mb-0">Chargement des adhérents…</p>
      </div>
    );
  }

  if (rootMembers.length === 0) {
    return (
      <div className="ap-mem-state">
        <h6>{isArchiveView ? 'Aucun adhérent archivé' : 'Aucun adhérent trouvé'}</h6>
        <p className="mb-0 small">
          {isArchiveView
            ? 'Les adhérents débauchés apparaîtront ici et pourront être réintégrés.'
            : hierarchical
              ? 'Modifiez les filtres ou créez un nouvel agent titulaire.'
              : 'Modifiez les filtres ou créez un nouvel adhérent.'}
        </p>
      </div>
    );
  }

  return (
    <>
      <div
        className={`ap-mem-table-wrap ${isAdminUi ? 'adm-mobile-table-wrap' : ''}`}
      >
        <table
          className={`ap-mem-table ap-mem-table--compact ${
            isAdminUi ? 'adm-mobile-table ap-mem-table--admin' : ''
          }`}
        >
          <thead>
            <tr>
              <th className="ap-mem-col-expand" aria-label="Déplier les ayants-droit">
                {selectable && (
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    className="ap-mem-select"
                    checked={pageAllSel}
                    onChange={onToggleSelectAll}
                    aria-label="Tout sélectionner sur cette page"
                    title="Tout sélectionner sur cette page"
                  />
                )}
              </th>
              <th>Adhérent</th>
              <th>Affectation</th>
              <th className="ap-mem-table__actions-col" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {rootMembers.map((memberItem) => {
              const isExpanded = Boolean(expandedAgents[memberItem.id]);
              const dependents = isExpanded ? expandedAgents[memberItem.id] : null;
              const isLoadingDeps = loadingDependents[memberItem.id];
              const isSelected = Boolean(selectedIds && selectedIds.has(memberItem.id));
              const menuOpen = actionMember?.id === memberItem.id;

              return (
                <React.Fragment key={memberItem.id}>
                  <MemberRow
                    member={memberItem}
                    isChild={false}
                    isSelected={isSelected}
                    isMenuOpen={menuOpen}
                    getCompanyName={getCompanyName}
                    formatDate={formatDate}
                    menuCanToggle
                    uiVariant={uiVariant}
                    onOpenMenu={openMenu}
                    expandCell={
                      <div className="ap-mem-lead">
                        {selectable && (
                          <input
                            type="checkbox"
                            className="ap-mem-select"
                            checked={isSelected}
                            onChange={() => onToggleSelect(memberItem)}
                            aria-label="Sélectionner cet adhérent"
                          />
                        )}
                        {hierarchical ? (
                          <AgentExpandButton
                            agent={memberItem}
                            isExpanded={isExpanded}
                            isLoading={isLoadingDeps}
                            onToggle={onToggleAgent}
                          />
                        ) : (
                          <span className="ap-mem-tree-gap" aria-hidden />
                        )}
                      </div>
                    }
                  />

                  {hierarchical && isExpanded && (
                    <>
                      {dependents.length === 0 ? (
                        <tr className="ap-mem-row--empty">
                          <td />
                          <td colSpan={3}>
                            <div className="ap-mem-empty-deps">
                              Aucun ayant-droit enregistré pour ce titulaire
                            </div>
                          </td>
                        </tr>
                      ) : (
                        dependents.map((dep) => (
                          <MemberRow
                            key={dep.id}
                            member={dep}
                            isChild
                            isMenuOpen={actionMember?.id === dep.id}
                            getCompanyName={getCompanyName}
                            formatDate={formatDate}
                            menuCanToggle={false}
                            uiVariant={uiVariant}
                            onOpenMenu={openMenu}
                            expandCell={<span className="ap-mem-tree-gap" aria-hidden />}
                          />
                        ))
                      )}
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <MemberRowActionMenu
        anchorEl={actionMenuAnchor}
        member={actionMember}
        onClose={closeActionMenu}
        onView={onView}
        onToggleStatus={onToggleStatus}
        onArchive={onArchive}
        onRestore={onRestore}
        onDelete={onDelete}
        getCompanyName={getCompanyName}
        formatDate={formatDate}
        isArchiveView={isArchiveView}
        menuCanToggle={menuCanToggle}
        theme={isAdminUi ? 'blue' : 'blue'}
      />
    </>
  );
};

export default MemberHierarchyTable;
